import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Phone, Shield, ArrowRight, AlertCircle, KeyRound, Store, Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import ForgotPinDialog from "@/pages/ForgotPinDialog";
import ShopForgotPinDialog from "@/pages/ShopForgotPinDialog";

// ---------- Member section ----------
const REMEMBER_MEMBER_KEY = "veloskin.remember.member.phone";

function MemberSignIn() {
  const { login } = useAuth();
  // stage: "phone" (initial) | "code" (first-timer) | "pin" (returning)
  const [stage, setStage] = useState("phone");
  const [phone, setPhone] = useState(() => localStorage.getItem(REMEMBER_MEMBER_KEY) || "");
  const [code, setCode] = useState("");
  const [pin, setPin] = useState("");
  const [nameHint, setNameHint] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [lockedMsg, setLockedMsg] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);
  const [remember, setRemember] = useState(() => !!localStorage.getItem(REMEMBER_MEMBER_KEY));

  const clearErrors = () => { setErr(""); setLockedMsg(""); };
  const back = () => { clearErrors(); setCode(""); setPin(""); setStage("phone"); };

  const persistRemember = () => {
    if (remember) localStorage.setItem(REMEMBER_MEMBER_KEY, phone);
    else localStorage.removeItem(REMEMBER_MEMBER_KEY);
  };

  const submitPhone = async () => {
    if (!phone || phone.length < 6) return toast.error("Enter your phone number");
    setBusy(true); clearErrors();
    try {
      const { data } = await api.post("/auth/login-check", { phone });
      setNameHint(data.name_hint || "");
      setStage(data.has_pin ? "pin" : "code");
    } catch (e) {
      const status = e.response?.status;
      const msg = e.response?.data?.detail || "Could not look up account";
      if (status === 404) setErr("not_registered");
      else if (status === 429) setLockedMsg(msg);
      else toast.error(msg);
    } finally { setBusy(false); }
  };

  const submitCode = async () => {
    if (!code) return toast.error("Enter your access code");
    setBusy(true); clearErrors();
    try {
      const { data } = await api.post("/auth/login", { phone, code });
      persistRemember();
      login(data.token, data.user);
      toast.success(`You're in!`);
    } catch (e) {
      const status = e.response?.status;
      const msg = e.response?.data?.detail || "Login failed";
      if (status === 401) setErr("wrong_code");
      else if (status === 429) setLockedMsg(msg);
      else toast.error(msg);
    } finally { setBusy(false); }
  };

  const submitPin = async () => {
    if (!/^\d{4}$/.test(pin)) return toast.error("Enter your 4-digit PIN");
    setBusy(true); clearErrors();
    try {
      const { data } = await api.post("/auth/pin-login", { phone, pin });
      persistRemember();
      login(data.token, data.user);
      toast.success(`Welcome back, ${data.user.name}`);
    } catch (e) {
      const status = e.response?.status;
      const msg = e.response?.data?.detail || "Login failed";
      if (status === 401) setErr("wrong_pin");
      else if (status === 429) setLockedMsg(msg);
      else toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <>
      {stage === "phone" && (
        <div data-testid="member-stage-phone" className="space-y-5">
          <div>
            <div className="text-white text-lg font-medium mb-1">Enter your phone number to get started.</div>
          </div>
          <div>
            <Label className="text-slate-300 text-xs uppercase tracking-wider">Phone number</Label>
            <Input
              data-testid="input-phone"
              value={phone}
              onChange={(e) => { setPhone(e.target.value); clearErrors(); }}
              onKeyDown={(e) => e.key === "Enter" && submitPhone()}
              placeholder="Enter your phone number"
              className="velo-input mt-2 h-12 font-mono"
              autoFocus
            />
          </div>
          {err === "not_registered" && (
            <div data-testid="not-registered-banner" className="p-4 rounded-lg border border-amber-500/30 bg-amber-500/10 flex items-start gap-3">
              <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" strokeWidth={2} />
              <div>
                <div className="text-sm text-amber-200 font-medium">Your number is not registered.</div>
                <div className="text-xs text-amber-300/80 mt-1">Please contact Veloskin Agility Club to be added to the loyalty programme.</div>
              </div>
            </div>
          )}
          {lockedMsg && (
            <div data-testid="locked-banner" className="p-4 rounded-lg border border-red-500/40 bg-red-500/10 flex items-start gap-3">
              <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" strokeWidth={2} />
              <div className="text-sm text-red-200">{lockedMsg}</div>
            </div>
          )}
          <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer select-none">
            <input
              data-testid="check-member-remember"
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="w-4 h-4 rounded border-white/20 bg-transparent accent-pink-500"
            />
            Remember me
          </label>
          <Button data-testid="btn-phone-continue" onClick={submitPhone} disabled={busy} className="w-full h-12 rounded-full bg-pink-500 hover:bg-pink-400 text-black font-medium">
            Continue <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
          </Button>
        </div>
      )}

      {stage === "code" && (
        <div data-testid="member-stage-code" className="space-y-5">
          <button onClick={back} className="text-xs text-slate-400 hover:text-white flex items-center gap-1.5">
            <ArrowLeft className="w-3.5 h-3.5" /> Change phone
          </button>
          <div className="text-white text-lg font-medium">
            {nameHint ? `Hi ${nameHint} — ` : ""}Enter your access code — first 4 letters of your name + last 4 digits of your phone number.
          </div>
          <div className="text-xs text-slate-500 -mt-3">Phone: <span className="text-slate-300 font-mono">{phone}</span></div>
          <div>
            <Label className="text-slate-300 text-xs uppercase tracking-wider">Access code</Label>
            <Input
              data-testid="input-code"
              value={code}
              onChange={(e) => { setCode(e.target.value.toUpperCase()); clearErrors(); }}
              onKeyDown={(e) => e.key === "Enter" && submitCode()}
              placeholder="ABCD1234"
              maxLength={12}
              className="velo-input mt-2 h-14 font-mono tracking-[0.4em] text-center text-lg"
              autoFocus
            />
          </div>
          {err === "wrong_code" && (
            <div data-testid="wrong-code-banner" className="p-4 rounded-lg border border-red-500/30 bg-red-500/10 flex items-start gap-3">
              <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" strokeWidth={2} />
              <div className="text-sm text-red-200">Incorrect code. Please contact Veloskin Agility Club if you don't have it.</div>
            </div>
          )}
          {lockedMsg && (
            <div className="p-4 rounded-lg border border-red-500/40 bg-red-500/10 text-sm text-red-200">{lockedMsg}</div>
          )}
          <Button data-testid="btn-code-continue" onClick={submitCode} disabled={busy} className="w-full h-12 rounded-full bg-pink-500 hover:bg-pink-400 text-black font-medium">
            Continue <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
          </Button>
        </div>
      )}

      {stage === "pin" && (
        <div data-testid="member-stage-pin" className="space-y-5">
          <button onClick={back} className="text-xs text-slate-400 hover:text-white flex items-center gap-1.5">
            <ArrowLeft className="w-3.5 h-3.5" /> Change phone
          </button>
          <div className="text-white text-lg font-medium">
            {nameHint ? `Welcome back, ${nameHint}. ` : ""}Enter your phone number and PIN to sign in.
          </div>
          <div>
            <Label className="text-slate-300 text-xs uppercase tracking-wider">Phone number</Label>
            <Input
              data-testid="input-phone-pin"
              value={phone}
              onChange={(e) => { setPhone(e.target.value); clearErrors(); }}
              className="velo-input mt-2 h-12 font-mono"
            />
          </div>
          <div>
            <Label className="text-slate-300 text-xs uppercase tracking-wider flex items-center gap-2">
              <KeyRound className="w-3.5 h-3.5" strokeWidth={2} /> 4-digit PIN
            </Label>
            <Input
              data-testid="input-pin"
              type="password"
              inputMode="numeric"
              value={pin}
              onChange={(e) => { setPin(e.target.value.replace(/\D/g, "")); clearErrors(); }}
              onKeyDown={(e) => e.key === "Enter" && submitPin()}
              placeholder="••••"
              maxLength={4}
              className="velo-input mt-2 h-14 font-mono tracking-[0.5em] text-center text-xl"
              autoFocus
            />
          </div>
          {err === "wrong_pin" && (
            <div data-testid="wrong-pin-banner" className="p-4 rounded-lg border border-red-500/30 bg-red-500/10 text-sm text-red-200">Incorrect PIN. Try again or use Forgot PIN.</div>
          )}
          {lockedMsg && (
            <div data-testid="locked-banner" className="p-4 rounded-lg border border-red-500/40 bg-red-500/10 text-sm text-red-200">{lockedMsg}</div>
          )}
          <Button data-testid="btn-signin" onClick={submitPin} disabled={busy} className="w-full h-12 rounded-full bg-pink-500 hover:bg-pink-400 text-black font-medium">
            Sign in <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
          </Button>
          <div className="text-center">
            <button data-testid="btn-forgot-pin" onClick={() => setForgotOpen(true)} className="text-xs text-pink-400 hover:text-pink-300 underline underline-offset-4">Forgot PIN?</button>
          </div>
        </div>
      )}

      <ForgotPinDialog open={forgotOpen} onOpenChange={setForgotOpen} initialPhone={phone} />
    </>
  );
}

// ---------- Shop section — mirrors MemberSignIn ----------
const REMEMBER_SHOP_KEY = "veloskin.remember.shop.phone";

function ShopSignIn() {
  const { loginShop } = useAuth();
  // stage: "phone" (initial) | "code" (first-timer) | "pin" (returning)
  const [stage, setStage] = useState("phone");
  const [phone, setPhone] = useState(() => localStorage.getItem(REMEMBER_SHOP_KEY) || "");
  const [code, setCode] = useState("");
  const [pin, setPin] = useState("");
  const [nameHint, setNameHint] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [lockedMsg, setLockedMsg] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);
  const [remember, setRemember] = useState(() => !!localStorage.getItem(REMEMBER_SHOP_KEY));

  const clearErrors = () => { setErr(""); setLockedMsg(""); };
  const back = () => { clearErrors(); setCode(""); setPin(""); setStage("phone"); };

  const persistRemember = () => {
    if (remember) localStorage.setItem(REMEMBER_SHOP_KEY, phone);
    else localStorage.removeItem(REMEMBER_SHOP_KEY);
  };

  const submitPhone = async () => {
    if (!phone || phone.length < 6) return toast.error("Enter your shop phone number");
    setBusy(true); clearErrors();
    try {
      const { data } = await api.post("/shop/login-check", { phone });
      setNameHint(data.name_hint || "");
      setStage(data.has_pin ? "pin" : "code");
    } catch (e) {
      const status = e.response?.status;
      const msg = e.response?.data?.detail || "Could not look up shop";
      if (status === 404) setErr("not_found");
      else if (status === 429) setLockedMsg(msg);
      else toast.error(msg);
    } finally { setBusy(false); }
  };

  const submitCode = async () => {
    if (!code) return toast.error("Enter your shop access code");
    setBusy(true); clearErrors();
    try {
      const { data } = await api.post("/shop/login", { phone, code });
      persistRemember();
      loginShop(data.token, data.shop);
      toast.success("You're in!");
    } catch (e) {
      const status = e.response?.status;
      const msg = e.response?.data?.detail || "Login failed";
      if (status === 401) setErr("wrong_code");
      else if (status === 429) setLockedMsg(msg);
      else toast.error(msg);
    } finally { setBusy(false); }
  };

  const submitPin = async () => {
    if (!/^\d{4}$/.test(pin)) return toast.error("Enter your 4-digit PIN");
    setBusy(true); clearErrors();
    try {
      const { data } = await api.post("/shop/pin-login", { phone, pin });
      persistRemember();
      loginShop(data.token, data.shop);
      toast.success(`Welcome back, ${data.shop.name}`);
    } catch (e) {
      const status = e.response?.status;
      const msg = e.response?.data?.detail || "Login failed";
      if (status === 401) setErr("wrong_pin");
      else if (status === 429) setLockedMsg(msg);
      else toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <>
      {stage === "phone" && (
        <div data-testid="shop-stage-phone" className="space-y-5">
          <div>
            <h2 className="text-3xl font-light text-white mb-1">Tie-up shop sign-in</h2>
            <p className="text-xs text-slate-500 uppercase tracking-wider">Scan customer QR codes to redeem points</p>
          </div>
          <div className="text-white text-lg font-medium mb-1">Enter your shop phone number to get started.</div>
          <div>
            <Label className="text-slate-300 text-xs uppercase tracking-wider">Shop phone number</Label>
            <Input
              data-testid="input-shop-phone"
              value={phone}
              onChange={(e) => { setPhone(e.target.value); clearErrors(); }}
              onKeyDown={(e) => e.key === "Enter" && submitPhone()}
              placeholder="Registered shop phone"
              className="velo-input mt-2 h-12 font-mono"
              autoFocus
            />
          </div>
          {err === "not_found" && (
            <div data-testid="shop-not-found" className="p-4 rounded-lg border border-amber-500/30 bg-amber-500/10 text-sm text-amber-200">No shop is registered with this phone number. Please contact Veloskin Agility Club.</div>
          )}
          {lockedMsg && <div className="p-4 rounded-lg border border-red-500/40 bg-red-500/10 text-sm text-red-200">{lockedMsg}</div>}
          <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer select-none">
            <input
              data-testid="check-shop-remember"
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="w-4 h-4 rounded border-white/20 bg-transparent accent-pink-500"
            />
            Remember me
          </label>
          <Button data-testid="btn-shop-phone-continue" onClick={submitPhone} disabled={busy} className="w-full h-12 rounded-full bg-pink-500 hover:bg-pink-400 text-black font-medium">
            Continue <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
          </Button>
        </div>
      )}

      {stage === "code" && (
        <div data-testid="shop-stage-code" className="space-y-5">
          <button onClick={back} className="text-xs text-slate-400 hover:text-white flex items-center gap-1.5"><ArrowLeft className="w-3.5 h-3.5" /> Change phone</button>
          <div className="text-white text-lg font-medium">
            {nameHint ? `Hi ${nameHint} — ` : ""}Enter your access code — first 4 letters of your shop name + last 4 digits of your phone number.
          </div>
          <div className="text-xs text-slate-500 -mt-3">Phone: <span className="text-slate-300 font-mono">{phone}</span></div>
          <div>
            <Label className="text-slate-300 text-xs uppercase tracking-wider">Access code</Label>
            <Input
              data-testid="input-shop-code"
              value={code}
              onChange={(e) => { setCode(e.target.value.toUpperCase()); clearErrors(); }}
              onKeyDown={(e) => e.key === "Enter" && submitCode()}
              placeholder="ABCD1234"
              maxLength={12}
              className="velo-input mt-2 h-14 font-mono tracking-[0.4em] text-center text-lg"
              autoFocus
            />
          </div>
          {err === "wrong_code" && (
            <div data-testid="shop-wrong-code" className="p-4 rounded-lg border border-red-500/30 bg-red-500/10 text-sm text-red-200">Incorrect code. Please contact Veloskin Agility Club if you don't have it.</div>
          )}
          {lockedMsg && <div className="p-4 rounded-lg border border-red-500/40 bg-red-500/10 text-sm text-red-200">{lockedMsg}</div>}
          <Button data-testid="btn-shop-code-continue" onClick={submitCode} disabled={busy} className="w-full h-12 rounded-full bg-pink-500 hover:bg-pink-400 text-black font-medium">
            Continue <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
          </Button>
        </div>
      )}

      {stage === "pin" && (
        <div data-testid="shop-stage-pin" className="space-y-5">
          <button onClick={back} className="text-xs text-slate-400 hover:text-white flex items-center gap-1.5"><ArrowLeft className="w-3.5 h-3.5" /> Change phone</button>
          <div className="text-white text-lg font-medium">
            {nameHint ? `Welcome back, ${nameHint}. ` : ""}Enter your phone number and PIN to sign in.
          </div>
          <div>
            <Label className="text-slate-300 text-xs uppercase tracking-wider">Shop phone number</Label>
            <Input data-testid="input-shop-phone-pin" value={phone} onChange={(e) => { setPhone(e.target.value); clearErrors(); }} className="velo-input mt-2 h-12 font-mono" />
          </div>
          <div>
            <Label className="text-slate-300 text-xs uppercase tracking-wider flex items-center gap-2"><KeyRound className="w-3.5 h-3.5" /> 4-digit PIN</Label>
            <Input
              data-testid="input-shop-pin"
              type="password" inputMode="numeric"
              value={pin} onChange={(e) => { setPin(e.target.value.replace(/\D/g, "")); clearErrors(); }}
              onKeyDown={(e) => e.key === "Enter" && submitPin()}
              placeholder="••••" maxLength={4}
              className="velo-input mt-2 h-14 font-mono tracking-[0.5em] text-center text-xl"
              autoFocus
            />
          </div>
          {err === "wrong_pin" && (
            <div data-testid="shop-wrong-pin" className="p-4 rounded-lg border border-red-500/30 bg-red-500/10 text-sm text-red-200">Incorrect PIN. Try again or use Forgot PIN.</div>
          )}
          {lockedMsg && <div className="p-4 rounded-lg border border-red-500/40 bg-red-500/10 text-sm text-red-200">{lockedMsg}</div>}
          <Button data-testid="btn-shop-signin" onClick={submitPin} disabled={busy} className="w-full h-12 rounded-full bg-pink-500 hover:bg-pink-400 text-black font-medium">
            Sign in <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
          </Button>
          <div className="text-center">
            <button data-testid="btn-shop-forgot-pin" onClick={() => setForgotOpen(true)} className="text-xs text-pink-400 hover:text-pink-300 underline underline-offset-4">Forgot PIN?</button>
          </div>
        </div>
      )}

      <ShopForgotPinDialog open={forgotOpen} onOpenChange={setForgotOpen} initialPhone={phone} />
    </>
  );
}

// ---------- Admin section ----------
const REMEMBER_ADMIN_KEY = "veloskin.remember.admin.email";

function AdminSignIn() {
  const { login } = useAuth();
  const [email, setEmail] = useState(() => localStorage.getItem(REMEMBER_ADMIN_KEY) || "");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(() => !!localStorage.getItem(REMEMBER_ADMIN_KEY));
  const [busy, setBusy] = useState(false);
  const [lockedMsg, setLockedMsg] = useState("");

  const submit = async () => {
    setBusy(true); setLockedMsg("");
    try {
      const { data } = await api.post("/auth/admin-login", { email, password });
      if (remember) localStorage.setItem(REMEMBER_ADMIN_KEY, email);
      else localStorage.removeItem(REMEMBER_ADMIN_KEY);
      login(data.token, data.user);
      toast.success("Welcome, Admin");
    } catch (e) {
      const status = e.response?.status;
      const msg = e.response?.data?.detail || "Invalid credentials";
      if (status === 429) setLockedMsg(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <h2 className="text-3xl font-light text-white">Admin console</h2>
      <div>
        <Label className="text-slate-300 text-xs uppercase tracking-wider">Email</Label>
        <Input data-testid="input-admin-email" value={email} onChange={(e) => setEmail(e.target.value)} className="velo-input mt-2 h-12" autoComplete="username" />
      </div>
      <div>
        <Label className="text-slate-300 text-xs uppercase tracking-wider">Password</Label>
        <Input data-testid="input-admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="velo-input mt-2 h-12" onKeyDown={(e) => e.key === "Enter" && submit()} autoComplete="current-password" />
      </div>
      <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer select-none">
        <input
          data-testid="check-admin-remember"
          type="checkbox"
          checked={remember}
          onChange={(e) => setRemember(e.target.checked)}
          className="w-4 h-4 rounded border-white/20 bg-transparent accent-pink-500"
        />
        Remember me
      </label>
      {lockedMsg && <div className="p-4 rounded-lg border border-red-500/40 bg-red-500/10 text-sm text-red-200">{lockedMsg}</div>}
      <Button data-testid="btn-admin-login" onClick={submit} disabled={busy} className="w-full h-12 rounded-full bg-pink-500 hover:bg-pink-400 text-black font-medium">
        Sign in as Admin <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
}

// ---------- Page ----------
export default function Login() {
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block overflow-hidden bg-[#150733]">
        {/* Brand radial glow (magenta top-left, deeper purple bottom-right) — pure purple canvas, no photography. */}
        <div className="absolute inset-0" style={{
          backgroundImage: "radial-gradient(circle at 18% 12%, rgba(224,26,142,0.22), transparent 45%), radial-gradient(circle at 82% 88%, rgba(74,20,112,0.55), transparent 52%)"
        }} />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <img src="/veloskin-logo.png" alt="Veloskin Agility Club" className="h-14 w-auto drop-shadow-[0_0_18px_rgba(224,26,142,0.35)]" />
            <div>
              <div className="text-white text-xl font-semibold tracking-tight">Veloskin Agility Club</div>
              <div className="text-xs text-slate-400 uppercase tracking-[0.2em]">Loyalty · Referral · Rewards</div>
            </div>
          </div>
          <div className="max-w-md">
            <div className="text-xs text-pink-400 uppercase tracking-[0.25em] mb-4">Premium detailing · PPF · Ceramic</div>
            <h1 className="text-5xl font-light text-white leading-tight tracking-tight">
              Refer. Earn.<br /><span className="text-pink-400 font-medium">Redeem.</span>
            </h1>
            <p className="mt-6 text-slate-400 text-base leading-relaxed">
              A dedicated loyalty operating system for the Veloskin Agility Club — track referrals, credit points, and process cash payouts with precision.
            </p>
          </div>
          <div className="text-xs text-slate-500 uppercase tracking-[0.2em]">v1.0 · Operator platform</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-5 sm:p-6 lg:p-12 bg-[#150733]">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <img src="/veloskin-logo.png" alt="Veloskin Agility Club" className="h-14 w-auto drop-shadow-[0_0_18px_rgba(224,26,142,0.35)]" />
            <div className="text-white text-lg font-semibold tracking-tight">Veloskin Agility Club</div>
          </div>

          <Tabs defaultValue="user" className="w-full">
            <TabsList className="grid grid-cols-3 bg-[#170929] border border-white/10 p-1 rounded-lg">
              <TabsTrigger value="user" data-testid="tab-user-login" className="data-[state=active]:bg-pink-500 data-[state=active]:text-black rounded-md text-slate-300">
                <Phone className="w-4 h-4 mr-2" strokeWidth={1.5} />Member
              </TabsTrigger>
              <TabsTrigger value="admin" data-testid="tab-admin-login" className="data-[state=active]:bg-pink-500 data-[state=active]:text-black rounded-md text-slate-300">
                <Shield className="w-4 h-4 mr-2" strokeWidth={1.5} />Admin
              </TabsTrigger>
              <TabsTrigger value="shop" data-testid="tab-shop-login" className="data-[state=active]:bg-pink-500 data-[state=active]:text-black rounded-md text-slate-300">
                <Store className="w-4 h-4 mr-2" strokeWidth={1.5} />Shop
              </TabsTrigger>
            </TabsList>

            <TabsContent value="user" className="mt-8"><MemberSignIn /></TabsContent>
            <TabsContent value="admin" className="mt-8"><AdminSignIn /></TabsContent>
            <TabsContent value="shop" className="mt-8"><ShopSignIn /></TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
